import dayjs from '~/timeInit';

import UsersService from '~/services/UsersService';

import type { GetUsersResponse, UserLoginType, UserType } from '~/types/responses/users';

import { getUserTenant } from '~/utils/userIdentity';

import type {
  AttributeDescriptor,
  AttributeKind,
  MatchedAccountEntry,
  MatchedAttribute,
  RegisterAttributeParams,
  SearchResults,
  SearchType,
  SeedAccount,
  SimilarAccountRow,
  SubmittedSearch,
} from './types';

export const pickString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

export const sanitizeIp = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return trimmed.replace(/\/32$/, '');
};

export const isIpv4 = (value: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(value);

export const normalizeEmail = (value?: string) => value?.trim().toLowerCase() ?? undefined;

export const normalizeName = (value?: string) => value?.trim().replace(/\s+/g, ' ').toLowerCase() ?? undefined;

export const normalizeIdentifier = (value?: string) => value?.trim().toLowerCase() ?? undefined;

export const normalizePhoneDigits = (value?: string) => value?.replace(/\D/g, '') ?? undefined;

export const normalizePhonePrefix = (value?: string) => {
  const trimmed = value?.trim() ?? undefined;
  if (!trimmed) {
    return undefined;
  }

  if (trimmed.startsWith('+')) {
    return trimmed;
  }

  const digits = trimmed.split('_')[0]?.replace(/\D/g, '');

  if (!digits) {
    return undefined;
  }

  return `+${digits}`;
};

export const getIdMember = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.id_member ?? data.memberId);
};

export const getEmail = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.email);
};

export const getGivenName = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.givenName);
};

export const getSurname = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.surname);
};

export const getFullName = (data: Record<string, unknown>): string | undefined => {
  const fullName = pickString(data.full_name ?? data.fullName);
  if (fullName) {
    return fullName;
  }
  const givenName = getGivenName(data);
  const surname = getSurname(data);
  if (givenName || surname) {
    return [givenName, surname].filter(Boolean).join(' ');
  }
  return undefined;
};

export const getBrand = (data: Record<string, unknown>): string | undefined => {
  return getUserTenant(data);
};

export const getPostcode = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.postcode);
};

export const getCreatedAt = (data: Record<string, unknown>): string | undefined => {
  return pickString(data.created_date);
};

export const getLastLoginAt = (data: Record<string, unknown>): string | undefined => {
  const lastLogin = data.lastLogin;
  if (typeof lastLogin === 'object' && lastLogin !== null) {
    return pickString((lastLogin as Record<string, unknown>).login_date);
  }
  return undefined;
};

export const getPhoneDisplay = (data: Record<string, unknown>): string | undefined => {
  const prefix = pickString(data.phone_prefix);
  const phone = pickString(data.phone);

  if (!prefix && !phone) {
    return undefined;
  }

  const formattedPrefix = normalizePhonePrefix(prefix);
  const formattedPhone = phone ? phone.replace(/\s+/g, '') : undefined;

  return [formattedPrefix, formattedPhone].filter(Boolean).join(' ').trim() || undefined;
};

export const getPhoneDigits = (data: Record<string, unknown>): string | undefined => {
  const prefix = pickString(data.phone_prefix);
  const phone = pickString(data.phone);
  const combined = [prefix, phone].filter(Boolean).join('');

  return normalizePhoneDigits(combined);
};

export const getOrdersCount = (data?: Record<string, unknown>): number | undefined => {
  const ordersCount = Number(data?.number_of_purchases);

  return Number.isNaN(ordersCount) ? undefined : ordersCount;
};

export const formatDateTime = (value?: string) => {
  if (!value) {
    return undefined;
  }

  const date = dayjs(value);

  if (!date.isValid()) {
    return undefined;
  }

  return date.format('DD MMM YYYY · HH:mm');
};

export const formatSeedLabel = (seed: SeedAccount) => {
  if (seed.fullName && seed.email) {
    return `${seed.fullName} (${seed.email})`;
  }

  if (seed.fullName) {
    return seed.fullName;
  }

  if (seed.email) {
    return seed.email;
  }

  return seed.idMember;
};

export const formatSourceLabels = (sources: Set<string>, seedLabelMap: Map<string, string>) => {
  if (!sources.size) {
    return undefined;
  }

  const labels = Array.from(sources).map((source) =>
    source === 'search-input' ? 'Search input' : (seedLabelMap.get(source) ?? source),
  );

  return Array.from(new Set(labels)).join(', ');
};

export const getUniqueMatchLabels = (matches: Array<MatchedAttribute>): Array<string> => {
  const uniqueLabels = new Set<string>();

  matches.forEach((match) => {
    const label = match.label || 'Match';
    uniqueLabels.add(label);
  });

  let labels = Array.from(uniqueLabels);

  // If "Full name" exists, remove "First name" and "Last name" to avoid redundancy
  const hasFullNameMatch = labels.some((label) => label.toLowerCase() === 'full name');
  if (hasFullNameMatch) {
    labels = labels.filter((label) => {
      const lower = label.toLowerCase();
      return lower !== 'first name' && lower !== 'last name';
    });
  }

  return labels;
};

/**
 * Transforms a SeedAccount to SimilarAccountRow for display in DataGrid
 * Used for phone number searches where seeds are the primary results
 */
export function seedToRow(seed: SeedAccount, searchValue: string): SimilarAccountRow {
  return {
    id: seed.idMember,
    idMember: seed.idMember,
    email: seed.email,
    fullName: seed.fullName,
    phoneNumber: seed.phoneNumber ?? searchValue,
    ordersCount: seed.ordersCount,
    brand: seed.brand,
    tenant: seed.brand,
    postcode: seed.postcode,
    matchedAttributes: [
      {
        label: 'Phone number',
        value: seed.phoneNumber ?? searchValue,
        source: 'Search input',
      },
    ],
  };
}

export const normalizeAttributeValue = (kind: AttributeKind, value: string) => {
  switch (kind) {
    case 'email':
      return normalizeEmail(value);
    case 'phone':
      return normalizePhoneDigits(value);
    case 'fullName':
    case 'firstName':
    case 'lastName':
      return normalizeName(value);
    case 'identifier':
      return normalizeIdentifier(value);
    case 'login_ip':
      return value.trim();
    default:
      return value.trim();
  }
};

export const shouldSearchAttribute = (attribute: AttributeDescriptor) => {
  if (!attribute.queryValue) {
    return false;
  }

  if (
    (attribute.kind === 'fullName' || attribute.kind === 'firstName' || attribute.kind === 'lastName') &&
    attribute.queryValue.length < 3
  ) {
    return false;
  }

  if (attribute.kind === 'phone' && attribute.queryValue.length < 6) {
    return false;
  }

  if (attribute.kind === 'identifier' && attribute.queryValue.length < 4) {
    return false;
  }

  if (attribute.kind === 'login_ip' && !isIpv4(attribute.queryValue)) {
    return false;
  }

  return true;
};

export const candidateMatchesAttribute = (candidate: Record<string, unknown>, attribute: AttributeDescriptor) => {
  switch (attribute.kind) {
    case 'email':
      return normalizeEmail(getEmail(candidate)) === attribute.normalizedValue;
    case 'phone':
      return getPhoneDigits(candidate) === attribute.normalizedValue;
    case 'identifier':
      return normalizeIdentifier(getIdMember(candidate)) === attribute.normalizedValue;
    case 'fullName':
      return normalizeName(getFullName(candidate)) === attribute.normalizedValue;
    case 'firstName':
      return normalizeName(getGivenName(candidate)) === attribute.normalizedValue;
    case 'lastName':
      return normalizeName(getSurname(candidate)) === attribute.normalizedValue;
    case 'login_ip':
      return true;
    default:
      return false;
  }
};

export async function searchUsers(filter: string, brand: string, signal?: AbortSignal) {
  try {
    return await UsersService.getUsers({ filter, brand, limit: 5 }, { signal });
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      return [];
    }
    throw error;
  }
}

export async function fetchUserLogins(idMember: string): Promise<UserLoginType> {
  try {
    return await UsersService.getUserLogins(idMember, 50);
  } catch {
    return [];
  }
}

export async function fetchUserDetail(idMember: string, brand: string) {
  try {
    return await UsersService.getUser(idMember, { brand });
  } catch {
    return null;
  }
}

export function registerAttribute(attributes: Map<string, AttributeDescriptor>, params: RegisterAttributeParams): void {
  const { kind, value, label, sourceId, displayValue: providedDisplayValue, queryValue: providedQueryValue } = params;

  if (!value) {
    return;
  }

  const displayValue = providedDisplayValue ?? value;
  const queryValue = providedQueryValue ?? value;
  const normalizedValue = normalizeAttributeValue(kind, value);

  if (!normalizedValue) {
    return;
  }

  const key = `${kind}:${normalizedValue}`;
  const existing = attributes.get(key);

  if (existing) {
    existing.sources.add(sourceId);
    return;
  }

  attributes.set(key, {
    key,
    kind,
    label,
    displayValue,
    queryValue,
    normalizedValue,
    sources: new Set([sourceId]),
  });
}

export function buildAttributes(seeds: Array<SeedAccount>): Array<AttributeDescriptor> {
  const attributes = new Map<string, AttributeDescriptor>();

  // Register seed attributes
  seeds.forEach((seed) => {
    registerAttribute(attributes, {
      kind: 'identifier',
      value: seed.idMember,
      label: 'User ID',
      sourceId: seed.idMember,
    });
    registerAttribute(attributes, {
      kind: 'email',
      value: seed.email,
      label: 'Email',
      sourceId: seed.idMember,
    });
    registerAttribute(attributes, {
      kind: 'phone',
      value: seed.phoneDigits,
      label: 'Phone number',
      sourceId: seed.idMember,
      displayValue: seed.phoneNumber ?? seed.phoneDigits ?? '',
      queryValue: seed.phoneDigits ?? '',
    });
    registerAttribute(attributes, {
      kind: 'fullName',
      value: seed.fullName,
      label: 'Full name',
      sourceId: seed.idMember,
    });
    registerAttribute(attributes, {
      kind: 'firstName',
      value: seed.givenName,
      label: 'First name',
      sourceId: seed.idMember,
    });
    registerAttribute(attributes, {
      kind: 'lastName',
      value: seed.surname,
      label: 'Last name',
      sourceId: seed.idMember,
    });
    seed.loginIps.forEach((ip) =>
      registerAttribute(attributes, {
        kind: 'login_ip',
        value: ip,
        label: 'Login IP',
        sourceId: seed.idMember,
      }),
    );
  });

  return Array.from(attributes.values());
}

export async function findMatchingAccounts(params: {
  attributes: Array<AttributeDescriptor>;
  brand: string;
  seedIds: Set<string>;
  seedLabelMap: Map<string, string>;
  signal?: AbortSignal;
}): Promise<Array<SimilarAccountRow>> {
  const { attributes, brand, seedIds, seedLabelMap, signal } = params;
  const searchableAttributes = attributes.filter(shouldSearchAttribute);

  if (!searchableAttributes.length) {
    return [];
  }

  const matches = new Map<string, MatchedAccountEntry>();

  for (const attribute of searchableAttributes) {
    let results: Array<Record<string, unknown>> = [];

    try {
      results = (await UsersService.getUsers({ filter: attribute.queryValue, brand, limit: 50 }, { signal })) ?? [];
    } catch (error) {
      // Ignore fetch errors - we still have summary/basic data.
    }

    results.forEach((result) => {
      const idMember = getIdMember(result);

      if (!idMember || seedIds.has(idMember) || !candidateMatchesAttribute(result, attribute)) {
        return;
      }

      let entry = matches.get(idMember);
      if (!entry) {
        entry = { id: idMember, raw: result, matchedAttributes: [] };
        matches.set(idMember, entry);
      }

      entry.matchedAttributes.push({
        label: attribute.label,
        value: attribute.displayValue,
        source: formatSourceLabels(attribute.sources, seedLabelMap),
      });
    });
  }

  if (!matches.size) {
    return [];
  }

  const DETAIL_FETCH_LIMIT = 25;
  const detailIds = Array.from(matches.keys()).slice(0, DETAIL_FETCH_LIMIT);
  const detailedProfiles = new Map<string, Record<string, unknown>>();

  await Promise.all(
    detailIds.map(async (id) => {
      try {
        const detail = await UsersService.getUser(id, { brand });
        if (detail) {
          detailedProfiles.set(id, detail as Record<string, unknown>);
        }
      } catch {
        // Ignore detail fetch failures – we still have summary/basic data.
      }
    }),
  );

  const rows: Array<SimilarAccountRow> = Array.from(matches.values()).map((entry) => {
    const raw = detailedProfiles.get(entry.id) ?? entry.raw;

    const matchKey = (match: MatchedAttribute) => `${match.label ?? ''}|${match.value ?? ''}|${match.source ?? ''}`;
    const seenMatches = new Set(entry.matchedAttributes.map(matchKey));

    // Check for additional matching attributes beyond the initial search
    for (const attribute of searchableAttributes) {
      if (!candidateMatchesAttribute(raw, attribute)) {
        continue;
      }

      const sourceLabel = formatSourceLabels(attribute.sources, seedLabelMap);
      const key = `${attribute.label}|${attribute.displayValue}|${sourceLabel}`;

      if (!seenMatches.has(key)) {
        entry.matchedAttributes.push({
          label: attribute.label,
          value: attribute.displayValue,
          source: sourceLabel,
        });
        seenMatches.add(key);
      }
    }

    return {
      id: entry.id,
      idMember: entry.id,
      email: getEmail(raw),
      fullName: getFullName(raw),
      ordersCount: getOrdersCount(raw),
      phoneNumber: getPhoneDisplay(raw),
      brand: getBrand(raw),
      tenant: getBrand(raw),
      postcode: getPostcode(raw),
      createdAt: getCreatedAt(raw),
      lastLoginAt: getLastLoginAt(raw),
      matchedAttributes: entry.matchedAttributes,
    };
  });

  // Filter matches: only include if matches on phone OR email OR 3+ fields
  const filteredRows = rows.filter((row) => {
    const matchedAttributes = getUniqueMatchLabels(row.matchedAttributes);
    const hasPhoneMatch = matchedAttributes.some((attr) => attr === 'Phone number');
    const hasEmailMatch = matchedAttributes.some((attr) => attr === 'Email');
    const hasThreeOrMoreMatches = matchedAttributes.length >= 3;

    return hasPhoneMatch || hasEmailMatch || hasThreeOrMoreMatches;
  });

  filteredRows.sort((a, b) => (b.matchedAttributes?.length ?? 0) - (a.matchedAttributes?.length ?? 0));

  return filteredRows;
}

export async function fetchSeedAccounts(
  search: SubmittedSearch,
  brand: string,
  signal?: AbortSignal,
): Promise<Array<SeedAccount>> {
  const seedAccounts: Array<SeedAccount> = [];

  if (!search.value) {
    return seedAccounts;
  }

  const searchResults: GetUsersResponse = await searchUsers(search.value, brand, signal);

  if (!searchResults.length) {
    return seedAccounts;
  }

  const limit = Math.max(1, Math.min(searchResults.length, 5));
  const candidates: GetUsersResponse = searchResults.slice(0, limit);

  const seedPromises = candidates.map(async (candidate) => {
    const idMember = getIdMember(candidate);
    if (!idMember) {
      return null;
    }

    const detailed: UserType = await fetchUserDetail(idMember, brand);

    const userLogins: UserLoginType = await fetchUserLogins(idMember);

    const ipArray =
      userLogins
        ?.map((userLogin: Record<string, unknown>) => sanitizeIp(pickString(userLogin.ip_address)))
        .filter(Boolean) ?? [];

    const seedAccount: SeedAccount = {
      idMember,
      email: getEmail(detailed),
      fullName: getFullName(detailed),
      givenName: getGivenName(detailed),
      surname: getSurname(detailed),
      phoneNumber: getPhoneDisplay(detailed),
      phoneDigits: getPhoneDigits(detailed),
      ordersCount: getOrdersCount(detailed),
      brand: getBrand(detailed),
      postcode: getPostcode(detailed),
      loginIps: new Set(ipArray),
      raw: detailed,
    };

    return seedAccount;
  });

  const seedResults = await Promise.all(seedPromises);

  const seen = new Set<string>();

  for (let i = 0; i < seedResults.length; i++) {
    const seed = seedResults[i];
    if (seed && !seen.has(seed.idMember)) {
      seen.add(seed.idMember);
      seedAccounts.push(seed);
    }
  }

  return seedAccounts;
}

export async function findSimilarAccounts(params: {
  searchType: SearchType;
  searchValue: string;
  brand: string;
  signal?: AbortSignal;
}): Promise<SearchResults> {
  const { searchType, searchValue, brand, signal } = params;

  const submittedSearch: SubmittedSearch = {
    type: searchType,
    value: searchValue,
  };

  const seedAccounts = await fetchSeedAccounts(submittedSearch, brand, signal);

  if (!seedAccounts.length) {
    throw new Error('No accounts found matching the provided input.');
  }

  const seedLabelMap = new Map(seedAccounts.map((seed) => [seed.idMember, formatSeedLabel(seed)]));

  const attributes = buildAttributes(seedAccounts);

  const matches = await findMatchingAccounts({
    attributes,
    brand,
    seedIds: new Set(seedAccounts.map((seed) => seed.idMember)),
    seedLabelMap,
    signal,
  });

  return {
    seedAccounts: seedAccounts.map((seed) => seedToRow(seed, submittedSearch.value ?? seed.phoneNumber ?? '')),
    similarAccounts: matches,
  };
}
